/**
 * ============================================
 * LOCAL NEWS HANDLER - COMPLETE REWRITE
 * ============================================
 * Features:
 * - 5 second cache for testing
 * - Force refresh clears all caches
 * - Better logging and debugging
 * - Multiple fallback strategies
 * - Image validation with local fallbacks
 * - Processes ALL nearby cities
 */

// ============================================
// CONFIGURATION
// ============================================
const FIREBASE_FUNCTIONS_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net';

// Cache storage
const SHOWN_ARTICLES_CACHE = new Set();
const LOCATION_FETCH_CACHE = new Map();
const NEARBY_CITIES_CACHE = new Map();

// ✅ PRODUCTION: 30 minute cache
const CACHE_LIFETIME = 30 * 60 * 1000; // 30 minutes
const SESSION_START = Date.now();

console.log('✅ PRODUCTION MODE: Article cache = 30 minutes');

// ============================================
// CACHE MANAGEMENT
// ============================================

const isArticleShown = (url) => {
  return SHOWN_ARTICLES_CACHE.has(url);
};

const markArticleShown = (url) => {
  SHOWN_ARTICLES_CACHE.add(url);
  
  setTimeout(() => {
    SHOWN_ARTICLES_CACHE.delete(url);
    console.log(`⏰ Cache expired: ${url.substring(0, 60)}...`);
  }, CACHE_LIFETIME);
};

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const wasLocationFetchedToday = (locationKey) => {
  const cached = LOCATION_FETCH_CACHE.get(locationKey);
  if (!cached) return false;
  
  const today = new Date().toISOString().split('T')[0];
  const cachedDate = new Date(cached.timestamp).toISOString().split('T')[0];
  
  return cachedDate === today;
};

const markLocationFetched = (locationKey) => {
  LOCATION_FETCH_CACHE.set(locationKey, {
    timestamp: Date.now()
  });
};

// ============================================
// GET USER LOCATION
// ============================================

const getUserLocation = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      console.error('❌ Geolocation not supported');
      reject(new Error('Geolocation not available'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        console.log('📍 Browser location:', latitude.toFixed(4), longitude.toFixed(4));

        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            { signal: AbortSignal.timeout(8000) }
          );

          if (!response.ok) throw new Error('Geocoding failed');

          const data = await response.json();

          resolve({
            city: data.city || data.locality || 'Unknown City',
            state: data.principalSubdivision || data.principalSubdivisionCode || '',
            country: data.countryName || 'Unknown',
            countryCode: data.countryCode || '',
            lat: latitude,
            lon: longitude
          });
        } catch (error) {
          console.warn('⚠️ Reverse geocoding failed:', error.message);
          resolve({
            city: `Location (${latitude.toFixed(2)}, ${longitude.toFixed(2)})`,
            state: '',
            country: 'Unknown',
            countryCode: '',
            lat: latitude,
            lon: longitude
          });
        }
      },
      (error) => {
        console.error('❌ Geolocation error:', error.code, error.message);
        
        let errorMsg = 'Location permission denied';
        if (error.code === 1) errorMsg = 'Location permission denied';
        else if (error.code === 2) errorMsg = 'Location unavailable';
        else if (error.code === 3) errorMsg = 'Location request timed out';
        
        reject(new Error(errorMsg));
      },
      {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 600000
      }
    );
  });
};

// ============================================
// FIND NEARBY CITIES
// ============================================

const findNearbyCities = async (lat, lon, mainCity) => {
  try {
    console.log(`🌆 Finding nearby cities for ${mainCity}...`);

    const cacheKey = `${lat.toFixed(2)},${lon.toFixed(2)}`;
    const cached = NEARBY_CITIES_CACHE.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp < 30 * 60 * 1000)) {
      console.log('📦 Using cached nearby cities:', cached.cities.join(', '));
      return cached.cities;
    }

    const probeOffsets = [
      { lat: 0.1, lon: 0 },
      { lat: -0.1, lon: 0 },
      { lat: 0, lon: 0.1 },
      { lat: 0, lon: -0.1 },
      { lat: 0.15, lon: 0.15 },
      { lat: -0.15, lon: -0.15 }
    ];

    const foundCities = new Set();
    const fetchPromises = [];

    for (const offset of probeOffsets) {
      const probeLat = lat + offset.lat;
      const probeLon = lon + offset.lon;

      fetchPromises.push(
        fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${probeLat}&longitude=${probeLon}&localityLanguage=en`,
          { signal: AbortSignal.timeout(4000) }
        )
        .then(response => response.ok ? response.json() : null)
        .then(data => {
          if (data) {
            const cityName = data.city || data.locality;
            if (cityName && cityName !== mainCity && cityName.length > 2) {
              foundCities.add(cityName);
            }
          }
        })
        .catch(() => {})
      );
    }

    await Promise.allSettled(fetchPromises);

    const selected = Array.from(foundCities).slice(0, 3); // Up to 3 nearby cities
    
    NEARBY_CITIES_CACHE.set(cacheKey, {
      cities: selected,
      timestamp: Date.now()
    });
    
    if (selected.length > 0) {
      console.log(`✅ Found ${selected.length} nearby cities: ${selected.join(', ')}`);
    } else {
      console.log('⚠️ No nearby cities found');
    }
    
    return selected;

  } catch (error) {
    console.warn('⚠️ Error finding nearby cities:', error.message);
    return [];
  }
};

// ============================================
// FETCH NEWS FROM BACKEND
// ============================================

const fetchNewsFromYahoo = async (locationName, state = '') => {
  try {
    const locationKey = `${locationName}-${state}`.toLowerCase();
    
    console.log(`🔍 Fetching: ${locationName}${state ? ', ' + state : ''}`);

    const url = `${FIREBASE_FUNCTIONS_URL}/getLocalNews?location=${encodeURIComponent(locationName)}&state=${encodeURIComponent(state)}&_t=${Date.now()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      signal: AbortSignal.timeout(25000)
    });

    if (!response.ok) {
      console.error(`❌ Backend returned ${response.status}`);
      return [];
    }

    const data = await response.json();

    if (!data.success || !data.results || data.results.length === 0) {
      console.warn(`⚠️ No results for: ${locationName}`);
      return [];
    }

    markLocationFetched(locationKey);

    console.log(`✅ Backend returned ${data.results.length} articles${data.fromCache ? ' (cached)' : ' (fresh)'}`);

    const articles = data.results
      .map(item => {
        const pubDate = new Date(item.datePublished);
        const now = new Date();
        const hoursAgo = Math.floor((now - pubDate) / (1000 * 60 * 60));

        let timeAgo;
        if (hoursAgo < 1) {
          const minutesAgo = Math.floor((now - pubDate) / (1000 * 60));
          timeAgo = `${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago`;
        } else if (hoursAgo < 24) {
          timeAgo = `${hoursAgo} hour${hoursAgo !== 1 ? 's' : ''} ago`;
        } else {
          const daysAgo = Math.floor(hoursAgo / 24);
          timeAgo = `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
        }

        return {
          title: item.title,
          snippet: item.snippet || 'Click to read more',
          url: item.url,
          datePublished: item.datePublished,
          timeAgo: timeAgo,
          imageUrl: item.imageUrl,
          source: item.source || 'News',
          hoursAgo: hoursAgo
        };
      })
      .filter(article => {
        if (!article.imageUrl) return false;
        return true;
      });

    console.log(`✅ Valid articles with images: ${articles.length}`);

    return articles;

  } catch (error) {
    console.error(`❌ Error fetching news for ${locationName}:`, error.message);
    return [];
  }
};

// ============================================
// MAIN FETCH FUNCTION
// ============================================

export const fetchLocalNews = async (forceRefresh = false) => {
  try {
    console.log('\n╔════════════════════════════════════════════╗');
    console.log('║  🌍 LOCAL NEWS FETCH (30min cache)        ║');
    console.log('╚════════════════════════════════════════════╝');
    console.log(`⏰ Cache lifetime: ${CACHE_LIFETIME / (60 * 1000)} minutes`);
    console.log(`🔄 Force refresh: ${forceRefresh}`);
    console.log(`📦 Shown cache size: ${SHOWN_ARTICLES_CACHE.size}`);

    // ✅ CRITICAL: Always clear cache on force refresh
    if (forceRefresh) {
      console.log('\n🧹 FORCE REFRESH - Clearing ALL caches');
      SHOWN_ARTICLES_CACHE.clear();
      LOCATION_FETCH_CACHE.clear();
    } else if (SHOWN_ARTICLES_CACHE.size > 20) {
      console.log('\n🧹 Cache too large (>20) - auto clearing');
      SHOWN_ARTICLES_CACHE.clear();
    }

    // Get location
    const location = await getUserLocation();
    console.log('\n✅ Location:', {
      city: location.city,
      state: location.state,
      coords: `${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}`
    });

    // Find nearby cities
    console.log('\n🔄 Finding nearby cities...');
    const nearbyCityNames = await findNearbyCities(location.lat, location.lon, location.city);

    // ============================================
    // MAIN CITY NEWS
    // ============================================
    console.log('\n┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
    console.log(`┃  MAIN CITY: ${location.city.padEnd(28)}┃`);
    console.log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
    
    let mainCityArticles = await fetchNewsFromYahoo(location.city, location.state);
    
    const mainCityRaw = mainCityArticles.length;
    console.log(`📰 Raw articles: ${mainCityRaw}`);
    
    // Filter and log what's being filtered
    const filteredTitles = [];
    mainCityArticles = mainCityArticles.filter(a => {
      const isShown = isArticleShown(a.url);
      if (isShown) {
        filteredTitles.push(a.title.substring(0, 50));
      }
      return !isShown;
    });
    
    if (filteredTitles.length > 0) {
      console.log(`⏭️ Filtered out ${filteredTitles.length} (already shown):`);
      filteredTitles.forEach((title, idx) => {
        console.log(`   ${idx + 1}. ${title}...`);
      });
    }
    
    console.log(`✅ New articles: ${mainCityArticles.length}`);
    
    // ✅ Fallback: If all filtered, try state
    if (mainCityRaw > 0 && mainCityArticles.length === 0) {
      console.log('\n⚠️ ALL FILTERED - Trying state search as fallback');
      
      const stateArticles = await fetchNewsFromYahoo(location.state, '');
      mainCityArticles = stateArticles.filter(a => !isArticleShown(a.url)).slice(0, 5);
      
      console.log(`✅ State fallback: ${mainCityArticles.length} new articles`);
    }
    
    // Mark articles as shown (with 30min expiry)
    mainCityArticles.forEach((a, idx) => {
      markArticleShown(a.url);
      console.log(`   ✓ [${idx + 1}] ${a.title.substring(0, 50)}... (expires in 30min)`);
    });

    // ============================================
    // NEARBY CITIES NEWS
    // ============================================
    const nearbyCitiesData = [];
    
    if (nearbyCityNames.length > 0) {
      console.log('\n┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
      console.log('┃  NEARBY CITIES                         ┃');
      console.log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
      
      for (const cityName of nearbyCityNames) {
        console.log(`\n🏙️ ${cityName}:`);
        
        let articles = await fetchNewsFromYahoo(cityName, location.state);
        
        console.log(`   📰 Raw: ${articles.length}`);
        
        const beforeFilter = articles.length;
        articles = articles.filter(a => !isArticleShown(a.url));
        
        console.log(`   ✅ New: ${articles.length} (filtered ${beforeFilter - articles.length})`);
        
        if (articles.length > 0) {
          articles.forEach(a => markArticleShown(a.url));
          
          nearbyCitiesData.push({ 
            name: cityName, 
            articles: articles.slice(0, 3) // Max 3 per city
          });
          
          console.log(`   ✅ Added with ${articles.length} articles`);
          
          // Log the articles
          articles.slice(0, 3).forEach((a, idx) => {
            console.log(`      ${idx + 1}. ${a.title.substring(0, 45)}...`);
          });
        } else {
          console.log(`   ⚠️ No new articles`);
        }
        
        await delay(1000);
        
        // Stop at 2 nearby cities
        if (nearbyCitiesData.length >= 2) {
          console.log('\n   ✓ Got 2 nearby cities - stopping');
          break;
        }
      }
    } else {
      console.log('\n⚠️ No nearby cities found');
    }

    // ============================================
    // STATE NEWS (if needed)
    // ============================================
    let stateArticles = [];
    const currentTotal = mainCityArticles.length + 
      nearbyCitiesData.reduce((sum, c) => sum + c.articles.length, 0);
    
    if (currentTotal < 5 && location.state) {
      console.log('\n┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
      console.log('┃  STATE NEWS (need more articles)       ┃');
      console.log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
      
      stateArticles = await fetchNewsFromYahoo(location.state, '');
      stateArticles = stateArticles.filter(a => !isArticleShown(a.url)).slice(0, 5);
      stateArticles.forEach(a => markArticleShown(a.url));
      
      console.log(`✅ State: ${stateArticles.length} articles`);
    }

    const totalArticles = mainCityArticles.length +
      nearbyCitiesData.reduce((sum, city) => sum + city.articles.length, 0) +
      stateArticles.length;

    // ============================================
    // RESULTS SUMMARY
    // ============================================
    console.log('\n╔════════════════════════════════════════════╗');
    console.log('║  📊 FINAL RESULTS                          ║');
    console.log('╚════════════════════════════════════════════╝');
    console.log(`🏙️ Main city (${location.city}): ${mainCityArticles.length} articles`);
    
    if (nearbyCitiesData.length > 0) {
      console.log(`📍 Nearby cities: ${nearbyCitiesData.length} sources`);
      nearbyCitiesData.forEach((city, idx) => {
        console.log(`   ${idx + 1}. ${city.name}: ${city.articles.length} articles`);
      });
    }
    
    if (stateArticles.length > 0) {
      console.log(`🏛️ State (${location.state}): ${stateArticles.length} articles`);
    }
    
    console.log(`\n📊 TOTAL: ${totalArticles} articles`);
    console.log(`⏰ All articles expire in ${CACHE_LIFETIME / (60 * 1000)} minutes`);

    // ✅ Emergency fallback if 0 articles
    if (totalArticles === 0) {
      console.error('\n❌ CRITICAL: 0 articles - EMERGENCY FALLBACK');
      
      SHOWN_ARTICLES_CACHE.clear();
      LOCATION_FETCH_CACHE.clear();
      
      console.log('🔄 Attempting emergency state-wide search...');
      const emergencyArticles = await fetchNewsFromYahoo(location.state, '');
      mainCityArticles = emergencyArticles.slice(0, 5);
      
      if (mainCityArticles.length === 0) {
        throw new Error('No news articles found for your area. Please try again.');
      }
      
      console.log(`✅ Emergency fetch: ${mainCityArticles.length} articles`);
    }

    return {
      mainCity: {
        name: location.city,
        state: location.state,
        country: location.country,
        articles: mainCityArticles,
        coords: {
          latitude: location.lat,
          longitude: location.lon
        }
      },
      nearbyCities: nearbyCitiesData,
      stateNews: {
        name: location.state || location.country,
        articles: stateArticles
      },
      totalArticles: totalArticles,
      method: 'Yahoo News (30min Cache)',
      cacheLifetime: `${CACHE_LIFETIME / (60 * 1000)} minutes`
    };

  } catch (error) {
    console.error('\n❌ FATAL ERROR:', error);
    
    if (error.message.includes('Location permission')) {
      throw new Error('Please enable location access to get local news.');
    }
    if (error.message.includes('timeout')) {
      throw new Error('Location request timed out. Please try again.');
    }
    
    throw error;
  }
};

// ============================================
// FORMAT FOR PODCAST
// ============================================

export const formatLocalNewsForPodcast = (newsData, userName = null, previouslyMentioned = new Set()) => {
  if (!newsData || !newsData.mainCity) return '';

  const greeting = userName ? `${userName}, here's` : "Here's";
  const mainArticles = (newsData.mainCity.articles || [])
    .filter(a => !previouslyMentioned.has(a.url))
    .slice(0, 3);

  const locationStr = newsData.mainCity.state
    ? `${newsData.mainCity.name}, ${newsData.mainCity.state}`
    : newsData.mainCity.name;

  let content = '';

  if (mainArticles.length > 0) {
    content += `${greeting} what's happening in ${locationStr}. `;
    content += `${mainArticles[0].title}, reported ${mainArticles[0].timeAgo}. `;

    if (mainArticles.length > 1) {
      content += `Also, ${mainArticles[1].title}. `;
    }

    if (mainArticles.length > 2) {
      content += `And ${mainArticles[2].title}. `;
    }
  }

  const citiesWithNews = (newsData.nearbyCities || []).filter(c => c.articles.length > 0);

  if (citiesWithNews.length > 0) {
    citiesWithNews.slice(0, 2).forEach((city, idx) => {
      const unseenArticles = city.articles.filter(a => !previouslyMentioned.has(a.url));

      if (unseenArticles.length > 0) {
        const prefix = idx === 0 ? 'Over in' : 'And in';
        content += `${prefix} ${city.name}, ${unseenArticles[0].title}. `;
      }
    });
  }

  mainArticles.forEach(a => previouslyMentioned.add(a.url));
  citiesWithNews.forEach(city => {
    city.articles.forEach(a => previouslyMentioned.add(a.url));
  });

  return content.trim();
};

export default {
  fetchLocalNews,
  formatLocalNewsForPodcast,
  getUserLocation
};